import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Segmented, Space, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { useDebounce } from '@shared/hooks/useDebounce';
import { ROUTES } from '@shared/constants/routes';
import { campaignsApi } from '@api/campaigns';
import {
  CAMPAIGN_FILTER_STATUSES,
  toContentStatus,
  useCampaignStatusLabel,
  type CampaignListItem,
  type CampaignStatus,
} from '../types';

type StatusFilter = CampaignStatus | 'ALL';

export function CampaignListPage() {
  const { t } = useTranslation(['campaigns', 'common']);
  const navigate = useNavigate();
  const statusLabel = useCampaignStatusLabel();
  const canCreate = useCan(PERMISSIONS.CAMPAIGN_CREATE);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [languageId, setLanguageId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const debouncedSearch = useDebounce(search, 350);

  const { data, isFetching } = useQuery({
    queryKey: ['campaigns', { debouncedSearch, status, languageId, page }],
    queryFn: () =>
      campaignsApi.list({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        status: status === 'ALL' ? undefined : status,
        languageId,
        sortBy: 'updatedAt',
      }),
    placeholderData: (prev) => prev,
  });

  const statusOptions = useMemo<{ value: StatusFilter; label: string }[]>(
    () => [
      { value: 'ALL', label: t('table.filterAll') },
      ...CAMPAIGN_FILTER_STATUSES.map((s) => ({ value: s, label: statusLabel(s) })),
    ],
    [t, statusLabel],
  );

  const columns: ColumnsType<CampaignListItem> = [
    {
      title: t('table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => void navigate(ROUTES.CAMPAIGN_EDIT.replace(':id', r.id))}>
          {title || t('table.untitled')}
        </Button>
      ),
    },
    {
      title: t('common:labels.status'),
      key: 'status',
      render: (_, r) => (
        <Tooltip title={statusLabel(r.status)}>
          <span>
            <StatusBadge kind="content" status={toContentStatus(r.status)} size="sm" />
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('table.dates'),
      key: 'dates',
      render: (_, r) =>
        r.startDate || r.endDate
          ? `${r.startDate ? dayjs(r.startDate).format('DD.MM.YYYY') : '—'} – ${r.endDate ? dayjs(r.endDate).format('DD.MM.YYYY') : '—'}`
          : '—',
    },
    {
      title: t('common:labels.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('page.listTitle')}
        description={t('page.listDescription')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('page.listTitle') }]}
        actions={
          canCreate ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={() => void navigate(ROUTES.CAMPAIGN_NEW)}>
              {t('page.new')}
            </Button>
          ) : null
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder={t('table.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 240 }}
          />
          <Segmented<StatusFilter>
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={statusOptions}
          />
          <LanguagePicker
            value={languageId}
            onChange={(v) => {
              setLanguageId(v);
              setPage(1);
            }}
            allowClear
            placeholder={t('common:contentLanguage.filterAll')}
            style={{ minWidth: 160 }}
          />
        </Space>
        <Table<CampaignListItem>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isFetching}
          locale={{ emptyText: t('page.empty') }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </Card>
    </div>
  );
}
